import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Download, FileText } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#F59E0B";

export const BranchContribution = () => {
  const bcT = useTableSort([]);
  const [data, setData] = useState(null);
  const [view, setView] = useState("committee");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dashboard/branch-contribution")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Katkı analizi yüklenemedi"));
  }, []);

  const download = async (format) => {
    setBusy(true);
    try {
      const order = groups.flatMap((g) => bcT.applySort(g.items)).map((r) => `${r.branch}|${r.committee_no}`).join(",");
      const res = await api.get("/dashboard/branch-contribution/export", { params: { format, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sube_katki.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    const match = (r) => !term
      || r.branch.toLocaleLowerCase("tr").includes(term)
      || (r.name || "").toLocaleLowerCase("tr").includes(term)
      || String(r.committee_no) === term;
    const rows = data.rows.filter(match);
    const map = new Map();
    for (const r of rows) {
      const key = view === "committee" ? r.committee_no : r.branch;
      const g = map.get(key) || {
        key,
        title: view === "committee" ? `${r.committee_no}. ${r.name}` : r.branch,
        items: [], faal: 0, secured: 0, potential: 0, contribution: 0,
        target: r.committee_target, gap: r.committee_gap,
      };
      g.items.push(r);
      g.faal += r.faal;
      g.secured += r.secured;
      g.potential += r.potential;
      g.contribution += r.contribution;
      map.set(key, g);
    }
    const out = [...map.values()];
    out.forEach((g) => g.items.sort((a, b) => b.contribution - a.contribution));
    return out.sort((a, b) => b.contribution - a.contribution);
  }, [data, q, view]);

  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-4" data-testid="branch-contribution">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { l: "Toplam Katkı Kapasitesi", v: data.total_contribution, c: NAVY },
          { l: "Kazanılabilir Oy", v: data.total_potential, c: AMBER },
          { l: "Şube Sayısı", v: data.by_branch.length, c: GREEN },
          { l: "Şube × Komite Hücresi", v: data.rows.length, c: "#0891b2" },
        ].map((k) => (
          <Card key={k.l} className="p-3" data-testid={`bc-kpi-${k.l}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
            <div className="text-xs text-muted-foreground">{k.l}</div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-full sm:w-64" data-testid="bc-view-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="committee">Komite bazlı (hangi şube katkı sunar)</SelectItem>
            <SelectItem value="branch">Şube bazlı (hangi komitelere katkı)</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Şube veya komite ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="bc-search" />
        </div>
        <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid="bc-export-xlsx">
          <Download className="h-4 w-4 mr-1.5" /> Excel
        </Button>
        <Button variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="bc-export-pdf">
          <FileText className="h-4 w-4 mr-1.5" /> PDF
        </Button>
      </div>

      <div className="space-y-3">
        {groups.map((g) => (
          <Card key={g.key} className="overflow-hidden" data-testid={`bc-group-${g.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
              <p className="font-semibold text-sm">{g.title}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>FAAL <b className="text-foreground">{g.faal}</b></span>
                <span>Kesin Oy <b style={{ color: GREEN }}>{g.secured}</b></span>
                <span>Kazanılabilir <b style={{ color: AMBER }}>{g.potential}</b></span>
                {view === "committee" && (
                  <span>Komite açığı <b style={{ color: g.gap === 0 ? GREEN : "#DC2626" }}>{g.gap === 0 ? "yok" : g.gap}</b></span>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-b border-border">
                  <tr>
                    <bcT.Th sortKey="bc2-ad" get={(r) => (view === "committee" ? r.branch : r.name)} text className="text-left font-medium py-2 px-3">{view === "committee" ? "Şube" : "Komite"}</bcT.Th>
                    <bcT.Th sortKey="bc2-faal" get={(r) => r.faal} className="text-right font-medium py-2 px-3">FAAL</bcT.Th>
                    <bcT.Th sortKey="bc2-kesin" get={(r) => r.secured} className="text-right font-medium py-2 px-3">Kesin Oy</bcT.Th>
                    <bcT.Th sortKey="bc2-potansiyel" get={(r) => r.potential} className="text-right font-medium py-2 px-3">Kazanılabilir</bcT.Th>
                    <bcT.Th sortKey="bc2-karsi" get={(r) => r.karsi} lowBest className="text-right font-medium py-2 px-3">Karşı</bcT.Th>
                    <bcT.Th sortKey="bc2-katki" get={(r) => r.contribution ?? r.secured} className="text-right font-medium py-2 px-3">Katkı</bcT.Th>
                    <bcT.Th sortKey="bc2-pay" get={(r) => r.share ?? 0} className="text-right font-medium py-2 px-3">Komite içi pay</bcT.Th>
                  </tr>
                </thead>
                <tbody>
                  {bcT.applySort(g.items).map((r) => (
                    <tr key={`${r.branch}-${r.committee_no}`} className="border-b border-border/50 hover:bg-muted/30"
                      data-testid={`bc-row-${r.branch}-${r.committee_no}`}>
                      <td className="py-2 px-3 font-medium">
                        {view === "committee" ? r.branch : `${r.committee_no}. ${r.name}`}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.faal}</td>
                      <td className="py-2 px-3 text-right tabular-nums" style={{ color: GREEN }}>{r.secured}</td>
                      <td className="py-2 px-3 text-right tabular-nums" style={{ color: AMBER }}>{r.potential}</td>
                      <td className="py-2 px-3 text-right tabular-nums" style={{ color: "#DC2626" }}>{r.karsi}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold">{r.contribution}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">%{r.share_pct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
        {groups.length === 0 && (
          <Card className="p-10 text-center text-sm text-muted-foreground">Kayıt bulunamadı</Card>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Katkı = şubenin o komitedeki FAAL firma sayısı + kazanılabilir oy (Belki / Ulaşılamadı / işaretsiz).
        Kesin Oy = KESİN eğilim veya yetki belgesi. Komite açığı = komite hedefi − kesin oy.
      </p>
    </div>
  );
};
